import axios from "axios";

const PLATFORM_HOSTS = new Set([
  "wacalls.com",
  "www.wacalls.com",
  "dev.wacalls.com",
  "www.dev.wacalls.com",
  "vicibots.com",
  "www.vicibots.com",
  "localhost",
  "127.0.0.1",
]);

function isPlatformHostname(host) {
  const h = (host || "").toLowerCase();
  return PLATFORM_HOSTS.has(h) || h.endsWith(".wacalls.com") || h.endsWith(".vicibots.com");
}

/** Use same-origin /api on reseller white-label domains (nginx proxies to backend). */
function resolveApiBase() {
  const configured = process.env.REACT_APP_BACKEND_URL;
  if (typeof window !== "undefined") {
    if (!isPlatformHostname(window.location.hostname)) return "/api";
  }
  return configured ? `${configured}/api` : "/api";
}

export function getApiBase() {
  return resolveApiBase();
}

export const API = getApiBase();

const api = axios.create({ baseURL: API });

// A per-tab sessionStorage token (used by impersonation tabs) takes precedence
// over the shared localStorage token, so a backdoor-login tab never disturbs the
// admin/reseller's own tab.
export function getStoredToken() {
  return sessionStorage.getItem("wa_token") || localStorage.getItem("wa_token");
}
export function clearStoredToken() {
  if (sessionStorage.getItem("wa_token")) sessionStorage.removeItem("wa_token");
  else localStorage.removeItem("wa_token");
}

// Backdoor login: mint a full-access token for the target account and open it in a
// NEW tab. The token is passed via the URL hash (never sent to the server/logs).
// The tab must be opened inside the click, before the request. Opening it after
// the await is no longer a user gesture, so the browser blocks the popup.
export async function openImpersonation(tenantId) {
  const popup = window.open("about:blank", "_blank");
  if (!popup) {
    const err = new Error("popup blocked");
    err.response = { data: { detail: "The browser blocked the new tab. Allow popups for this site, then try again." } };
    throw err;
  }
  // Give the tab real content immediately. A blank popup opened and then left
  // idle while the request runs is closed or blocked by the browser.
  try {
    popup.document.write("<!doctype html><title>Opening account…</title><p style='font-family:sans-serif'>Opening account…</p>");
    popup.document.close();
  } catch (_) { /* some browsers lock the document; navigation below still works */ }
  try {
    const { data } = await api.post("/auth/impersonate", { target_tenant_id: tenantId });
    if (popup.closed) {
      const err = new Error("popup closed");
      err.response = { data: { detail: "The new tab was closed before the account could open. Allow popups for this site, then try again." } };
      throw err;
    }
    const url = `${window.location.origin}/impersonate#t=${encodeURIComponent(data.access_token)}`;
    popup.location.replace(url);
    popup.focus();
    return data.target;
  } catch (e) {
    try { if (!popup.closed) popup.close(); } catch (_) { /* tab may already be gone */ }
    throw e;
  }
}

api.interceptors.request.use((config) => {
  // Resolve on every request — white-label domains must hit same-origin /api.
  config.baseURL = resolveApiBase();
  const token = getStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err.config?.url || "";
    const isAuthAttempt =
      url.includes("/auth/login")
      || url.includes("/auth/signup")
      || url.includes("/auth/forgot-password")
      || url.includes("/auth/reset-password");
    // Only force logout on real session-death responses — never on network blips
    // or unrelated 401s. Sessions stay until the user clicks Logout.
    const detail = err.response?.data?.detail;
    const detailStr = typeof detail === "string" ? detail : "";
    const sessionDead = (
      err.response?.status === 401
      && getStoredToken()
      && !isAuthAttempt
      && (
        /not authenticated|invalid token|user not found|token expired/i.test(detailStr)
        || url.includes("/auth/me")
      )
    );
    if (sessionDead) {
      clearStoredToken();
      if (window.location.pathname !== "/login") window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail, err) {
  if (detail == null) {
    const data = err?.response?.data;
    if (typeof data === "string" && data.trim()) return data.trim();
    if (err?.message === "Network Error") {
      return "Could not reach the server. Check your connection and try again.";
    }
    if (err?.message && !err?.response) {
      return "Could not reach the server. Check your connection and try again.";
    }
    return "Something went wrong. Please try again.";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function money(minor, currency = "USD") {
  const v = (Number(minor || 0) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `$${v}`;
}

/** Commission amounts stored as $ × 100000 (5 decimal places). */
export function money5(x1e5) {
  const v = (Number(x1e5 || 0) / 100000).toLocaleString("en-US", {
    minimumFractionDigits: 5,
    maximumFractionDigits: 5,
  });
  return `$${v}`;
}

export default api;
